import Icon from '@/components/Icon';
import { flexStyle, textStyle } from '@themes/mixins';
import styled, { css, keyframes } from 'styled-components';

import { TaskTone } from './types';

const toneColor = (tone: TaskTone) => css`
  ${({ theme }) => {
    switch (tone) {
      case 'done':
        return theme.colors.success;
      case 'error':
        return theme.colors.danger;
      case 'running':
        return theme.colors.primary;
      case 'queued':
      default:
        return theme.colors.textSecondary;
    }
  }}
`;

export const Root = styled.div`
  position: relative;
  display: inline-flex;
`;

export const BellButton = styled.div<{ open?: boolean }>`
  position: relative;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: background-color 0.2s ease-in-out;

  ${({ open, theme }) =>
    open &&
    css`
      background-color: ${theme.colors.surfaceSecondary};
    `}

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

const swing = keyframes`
  0% { transform: rotate(0deg); }
  15% { transform: rotate(12deg); }
  30% { transform: rotate(-10deg); }
  45% { transform: rotate(8deg); }
  60% { transform: rotate(-6deg); }
  75% { transform: rotate(3deg); }
  100% { transform: rotate(0deg); }
`;

export const BellIcon = styled(Icon)<{ $ringing?: boolean; open?: boolean }>`
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 6px;
  transform-origin: top center;
  transition: color 0.25s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  ${({ open, theme }) =>
    open &&
    css`
      color: ${theme.colors.textPrimary};
    `}

  ${({ $ringing, theme }) =>
    $ringing &&
    css`
      color: ${theme.colors.primary};
      animation: ${swing} 1s ease-in-out infinite;
    `}
`;

export const Badge = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.primary};
  ${textStyle({
    color: 'textWhite',
    size: '10px',
    weight: 600,
    textAlign: 'center',
    lineHeight: 1,
  })};
  ${flexStyle({ align: 'center', justify: 'center', direction: 'row' })};
  pointer-events: none;
`;

export const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1290;
`;

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Panel = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 360px;
  max-height: 70vh;
  z-index: 1300;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.paper};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 12px;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.35);
  overflow: hidden;
  animation: ${slideDown} 0.18s ease-out;
`;

export const PanelHeader = styled.div`
  ${flexStyle({ align: 'center', justify: 'space-between', direction: 'row' })};
  padding: 14px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

export const PanelTitle = styled.div`
  ${textStyle({ color: 'textPrimary', size: '14px', weight: 600 })};
`;

export const PanelCount = styled.div`
  ${textStyle({ color: 'textSecondary', size: '12px', weight: 500 })};
`;

export const HeaderActions = styled.div`
  ${flexStyle({ align: 'center', justify: 'flex-end', direction: 'row' })};
  gap: 10px;
`;

export const CancelAllButton = styled.button`
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 6px;
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.danger};
  ${textStyle({ size: '12px', weight: 600 })};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.danger};
    color: ${({ theme }) => theme.colors.textWhite};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const RowRight = styled.div`
  ${flexStyle({ align: 'center', justify: 'flex-end', direction: 'row' })};
  gap: 8px;
`;

export const RowCancelButton = styled.button`
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 6px;
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  color: ${({ theme }) => theme.colors.textSecondary};
  ${textStyle({ size: '11px', weight: 600 })};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.danger};
    color: ${({ theme }) => theme.colors.danger};
  }
`;

export const RowDownloadButton = styled.button`
  ${flexStyle({ align: 'center', justify: 'center', direction: 'row' })};
  gap: 4px;
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 6px;
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  ${textStyle({ size: '11px', weight: 600 })};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.textWhite};
  }
`;

export const TaskList = styled.div`
  overflow-y: auto;
  padding: 6px;
`;

export const TaskRow = styled.div`
  padding: 12px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.divider};

  & + & {
    margin-top: 8px;
  }
`;

export const TaskRowHeader = styled.div`
  ${flexStyle({ align: 'center', justify: 'space-between', direction: 'row' })};
  gap: 8px;
  margin-bottom: 8px;
`;

export const TypePill = styled.span`
  padding: 2px 8px;
  border-radius: 6px;
  background-color: ${({ theme }) => theme.colors.surfaceSecondary};
  ${textStyle({ color: 'textPrimary', size: '12px', weight: 600 })};
`;

export const StatusPill = styled.span<{ $tone: TaskTone }>`
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: capitalize;
  ${textStyle({ size: '11px', weight: 600 })};
  color: ${({ $tone }) => toneColor($tone)};
  border: 1px solid ${({ $tone }) => toneColor($tone)};
`;

export const StateText = styled.div`
  ${textStyle({ color: 'textSecondary', size: '12px', weight: 400 })};
  margin-bottom: 8px;
  word-break: break-word;
`;

/** Identifies which run a row belongs to: entity, run date, number of reports. */
export const SubtitleText = styled.div`
  ${textStyle({ color: 'textPrimary', size: '12px', weight: 500 })};
  margin-bottom: 4px;
  word-break: break-word;
`;

/**
 * Reports that failed inside an otherwise successful run. Muted on purpose: the workbook was still
 * produced and remains downloadable, so this is a caveat rather than a failure.
 */
export const WarningText = styled.div`
  ${textStyle({ color: 'warning', size: '11px', weight: 400 })};
  margin-bottom: 8px;
  word-break: break-word;
`;

export const ProgressTrack = styled.div`
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background-color: ${({ theme }) => theme.colors.secondaryBackground};
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ $value: number; $tone: TaskTone }>`
  height: 100%;
  border-radius: 3px;
  width: ${({ $value }) => Math.min(100, Math.max(0, $value))}%;
  background-color: ${({ $tone }) => toneColor($tone)};
  transition: width 0.3s ease;
`;

export const TaskMeta = styled.div`
  ${flexStyle({ align: 'center', justify: 'space-between', direction: 'row' })};
  gap: 8px;
  margin-top: 6px;
  ${textStyle({ color: 'textSecondary', size: '11px', weight: 400 })};
`;

export const EmptyState = styled.div`
  ${textStyle({ color: 'textSecondary', size: '13px', textAlign: 'center' })};
  padding: 28px 16px;
`;

export const PaginationBar = styled.div`
  ${flexStyle({ align: 'center', justify: 'space-between', direction: 'row' })};
  gap: 8px;
  padding: 10px 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
`;

export const PageInfo = styled.span`
  ${textStyle({ color: 'textSecondary', size: '12px', weight: 500 })};
`;

export const PageButtons = styled.div`
  ${flexStyle({ align: 'center', direction: 'row' })};
  gap: 6px;
`;

export const PageButton = styled.button`
  ${flexStyle({ align: 'center', justify: 'center', direction: 'row' })};
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.textPrimary};
    background-color: ${({ theme }) => theme.colors.surfaceSecondary};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;
